import React from 'react';
import { useParticipants, useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { COLORS, Z_INDEX, SPACING, BORDER_RADIUS } from '../../utils/constants';
import { getInitials } from '../../utils/helpers';
import { getParticipantDisplayName } from '../../utils/participantMeta';
import '../../styles/peopleTray.css';

function PeopleModal({
  isOpen,
  onClose,
  participantName,
  localParticipantIsHost = false,
  docked = false,
}) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();

  const localIdentity =
    localParticipant?.identity || room?.localParticipant?.identity || '';

  const isLocalParticipant = (participant) => {
    if (!participant?.identity) return false;
    if (localIdentity && participant.identity === localIdentity) return true;
    if (participant.isLocal === true) return true;
    return false;
  };

  const parseMetadata = (participant) => {
    try {
      let metadataString = null;
      if (room) {
        const remoteParticipant = room.remoteParticipants.get(participant.identity);
        if (remoteParticipant?.metadata) {
          metadataString = remoteParticipant.metadata;
        }
      }
      if (!metadataString && room?.localParticipant) {
        if (participant.identity === room.localParticipant.identity && room.localParticipant.metadata) {
          metadataString = room.localParticipant.metadata;
        }
      }
      if (!metadataString && participant.metadata) {
        metadataString = participant.metadata;
      }
      if (metadataString && metadataString.trim() !== '') {
        return typeof metadataString === 'string'
          ? JSON.parse(metadataString)
          : metadataString;
      }
    } catch (e) {
      console.error('Error parsing participant metadata:', e, participant);
    }
    return null;
  };

  const isAgent = (participant) => {
    if (participant?.isAgent) {
      return true;
    }
    // LiveKit ParticipantKind.AGENT (numeric enum varies by SDK version)
    const kind = participant?.kind;
    if (kind === 'agent' || kind === 'AGENT' || kind === 4) {
      return true;
    }
    const identity = (participant?.identity || '').toLowerCase();
    if (identity.includes('agent') || identity.includes('copilot') || identity.includes('sage')) {
      return true;
    }
    const metadata = parseMetadata(participant);
    const agentValue = metadata?.isAgent;
    return agentValue === true || agentValue === 'true' || agentValue === 1;
  };

  // Check if a participant is the host
  const isHost = (participant) => {
    try {
      if (isLocalParticipant(participant) && localParticipantIsHost) {
        return true;
      }
      
      const metadata = parseMetadata(participant);
      if (metadata?.role === 'host') return true;
      const hostValue = metadata?.isHost;
      if (hostValue === true || hostValue === 'true' || hostValue === 1) {
        return true;
      }
    } catch (e) {
      console.error('Error checking host:', e, participant);
    }
    return false;
  };

  const isCandidate = (participant) => {
    const metadata = parseMetadata(participant);
    return metadata?.role === 'candidate';
  };

  const isTeammate = (participant) => {
    const metadata = parseMetadata(participant);
    return metadata?.role === 'team';
  };

  // Sort participants: host first, then local participant, then others (hide agents)
  const visibleParticipants = participants.filter((p) => !isAgent(p));
  const participantCount = visibleParticipants.length;
  const sortedParticipants = [...visibleParticipants].sort((a, b) => {
    const aIsHost = isHost(a);
    const bIsHost = isHost(b);
    const aIsLocal = isLocalParticipant(a);
    const bIsLocal = isLocalParticipant(b);
    
    // Host first
    if (aIsHost && !bIsHost) return -1;
    if (!aIsHost && bIsHost) return 1;
    
    // Then local participant
    if (aIsLocal && !bIsLocal) return -1;
    if (!aIsLocal && bIsLocal) return 1;
    
    return 0;
  });

  const otherParticipants = sortedParticipants.filter(p => !isLocalParticipant(p));

  const borderColor = docked ? COLORS.border : COLORS.borderDark;
  const titleColor = docked ? COLORS.text : COLORS.textDark;
  const mutedColor = docked ? COLORS.textTertiary : COLORS.textTertiaryDark;

  const headerStyle = {
    padding: SPACING.xl,
    borderBottom: `1px solid ${borderColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: titleColor,
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: titleColor,
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: BORDER_RADIUS.sm,
    transition: 'background 0.2s ease',
  };

  const sectionHeaderStyle = {
    padding: `${SPACING.lg} ${SPACING.xl}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${borderColor}`,
  };

  const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: titleColor,
  };

  const sectionCountStyle = {
    fontSize: '14px',
    color: mutedColor,
  };

  const participantItemStyle = {
    padding: `${SPACING.lg} ${SPACING.xl}`,
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.md,
    borderBottom: `1px solid ${borderColor}`,
  };

  const avatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: BORDER_RADIUS.full,
    background: COLORS.primaryGradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.background,
    fontSize: '16px',
    fontWeight: '600',
    flexShrink: 0,
  };

  const participantInfoStyle = {
    flex: 1,
    minWidth: 0,
  };

  const participantNameStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: titleColor,
    marginBottom: SPACING.xs,
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.sm,
  };

  const participantRoleStyle = {
    fontSize: '12px',
    color: mutedColor,
  };

  const hostBadgeStyle = {
    background: COLORS.primaryGradient,
    color: COLORS.background,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.xs,
  };

  const menuButtonStyle = {
    background: docked ? '#f3f4f6' : COLORS.secondary,
    border: 'none',
    borderRadius: BORDER_RADIUS.full,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  };

  const emptyStateStyle = {
    padding: `${SPACING.xxxxl} ${SPACING.xl}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  };

  const emptyIconStyle = {
    width: '64px',
    height: '64px',
    marginBottom: SPACING.lg,
    opacity: 0.5,
  };

  const emptyTextStyle = {
    fontSize: '14px',
    color: mutedColor,
    marginBottom: SPACING.xs,
  };

  const handleCloseMouseEnter = (e) => {
    e.target.style.background = COLORS.backgroundGrey;
  };

  const handleCloseMouseLeave = (e) => {
    e.target.style.background = 'none';
  };

  const handleMenuMouseEnter = (e) => {
    e.target.style.background = COLORS.secondaryHover;
  };

  const handleMenuMouseLeave = (e) => {
    e.target.style.background = COLORS.secondary;
  };

  if (docked && !isOpen) {
    return null;
  }

  return (
    <>
      {!docked && isOpen ? (
        <div
          className="people-tray-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      ) : null}

      <div
        className={`people-tray${isOpen ? ' open' : ''}${
          docked ? ' people-tray--docked' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <h2 style={titleStyle}>People</h2>
          <button
            style={closeButtonStyle}
            onClick={onClose}
            onMouseEnter={handleCloseMouseEnter}
            onMouseLeave={handleCloseMouseLeave}
            className="people-tray-close"
            type="button"
          >
            ×
          </button>
        </div>

        <div style={sectionHeaderStyle}>
          <span style={sectionTitleStyle}>Contributors</span>
          <span style={sectionCountStyle}>{participantCount}</span>
        </div>

        <div className="people-tray-content" style={{ overflowY: 'auto', flex: 1 }}>
          {sortedParticipants.map((participant) => {
            const isLocal = isLocalParticipant(participant);
            const participantIsHost = isHost(participant);
            const name = getParticipantDisplayName(
              participant,
              isLocal ? participantName : ''
            );
            const displayName = isLocal ? `${name} (You)` : name;

            return (
              <div key={participant.identity} style={participantItemStyle}>
                <div style={avatarStyle}>
                  {getInitials(name)}
                </div>
                <div style={participantInfoStyle}>
                  <div style={participantNameStyle}>
                    {displayName}
                    {participantIsHost && (
                      <span style={hostBadgeStyle}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white"/>
                        </svg>
                        Host
                      </span>
                    )}
                    {!participantIsHost && isTeammate(participant) && (
                      <span style={{ ...hostBadgeStyle, background: '#6366f1' }}>
                        Teammate
                      </span>
                    )}
                    {!participantIsHost && isCandidate(participant) && (
                      <span style={{ ...hostBadgeStyle, background: COLORS.secondary }}>
                        Candidate
                      </span>
                    )}
                  </div>
                  <div style={participantRoleStyle}>
                    {participantIsHost
                      ? 'Host'
                      : isTeammate(participant)
                        ? 'Teammate'
                        : isCandidate(participant)
                          ? 'Candidate'
                          : 'Participant'}
                  </div>
                </div>
                {/* TODO: participant options menu (mute / remove / etc.)
                <button
                  type="button"
                  style={menuButtonStyle}
                  onMouseEnter={handleMenuMouseEnter}
                  onMouseLeave={handleMenuMouseLeave}
                  aria-label="Participant options"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="6" r="1.5" fill={docked ? '#6b7280' : 'white'}/>
                    <circle cx="12" cy="12" r="1.5" fill={docked ? '#6b7280' : 'white'}/>
                    <circle cx="12" cy="18" r="1.5" fill={docked ? '#6b7280' : 'white'}/>
                  </svg>
                </button>
                */}
              </div>
            );
          })}

          {otherParticipants.length === 0 && (
            <div style={emptyStateStyle}>
              <svg style={emptyIconStyle} width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 8L16 12L20 16" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p style={emptyTextStyle}>No other participants yet</p>
              <p style={emptyTextStyle}>Share the meeting link to invite others</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PeopleModal;

